import { Pressable, View, Platform } from "react-native"
import { Header } from "components/Header"
import { ar } from "i18n/ar"
import { Show, useObservable } from "@legendapp/state/react"
import { transferFromWalletInput, transferToWalletInput } from "@white-labels/input/src/m5azen"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import Icon from "@react-native-vector-icons/material-design-icons"
import { trpc } from "utils/trpc"
import { PriceInput } from "@src/components/price-input"
import { Button, Text, ModalWrapper } from "components/core"
import { useEffect } from "react"

export default function CashAndWallet() {
  const activeModal$ = useObservable<"none" | "transfer" | "receive" | "charge">("none")

  function handleModal(modal: "transfer" | "receive" | "charge") {
    activeModal$.set(modal)
  }

  function hide() {
    activeModal$.set("none")
  }

  return (
    <>
      <Header title={ar.wallet} />
      <View className="flex-1 bg-gray-50">
        <View className="px-4 md:px-6 lg:px-8 pt-6 max-w-content-wide mx-auto w-full flex-row gap-x-4 justify-center">
          <Pressable
            className="max-w-[300px] flex-1 bg-[#898]  rounded shadow-xl mb-4 p-6 md:p-8 items-center justify-center aspect-3/2 md:aspect-2/1 active:opacity-90 hover:scale-105 transition-transform"
            onPress={() => handleModal("transfer")}
          >
            <Icon name="arrow-up-circle" size={48} color="white" style={{ marginBottom: 12 }} />
            <Text className="text-center text-white font-bold text-xl md:text-2xl">{ar.transfer}</Text>
          </Pressable>

          <Pressable
            className="max-w-[300px] flex-1 bg-[#08909C]  rounded shadow-xl mb-4 p-6 md:p-8 items-center justify-center aspect-3/2 md:aspect-2/1 active:opacity-90 hover:scale-105 transition-transform"
            onPress={() => handleModal("receive")}
          >
            <Icon name="arrow-down-circle" size={48} color="white" style={{ marginBottom: 12 }} />
            <Text className="text-center text-white font-bold text-xl md:text-2xl">{ar.receive}</Text>
          </Pressable>
          {/* </View> */}
        </View>
      </View>

      <Show if={() => activeModal$.get() === "transfer"}>
        <FromWalletTransferModal onClose={hide} />
      </Show>

      <Show if={() => activeModal$.get() === "receive"}>
        <ToWalletTransferModal onClose={hide} />
      </Show>
    </>
  )
}

function FromWalletTransferModal({ onClose }: { onClose: () => void }) {
  const fromWalletTransfer = trpc.balance.fromWalletTransfer.useMutation()
  const utils = trpc.useUtils()
  const { control, formState, handleSubmit } = useForm({
    resolver: zodResolver(transferFromWalletInput),
  })
  const { isValid } = formState
  const disabled = !isValid

  const onSubmit = handleSubmit((data) => {
    fromWalletTransfer.mutateAsync(data)
    utils.balance.get.invalidate()
    utils.balance.track.invalidate()
    onClose()
  })

  useEffect(() => {
    if (Platform.OS === "web") {
      const handler = (e: KeyboardEvent) => {
        if (e.key === "Escape") onClose()
      }
      if (typeof window !== "undefined") {
        window.addEventListener("keydown", handler)
        return () => window.removeEventListener("keydown", handler)
      }
    }
  }, [onClose])

  return (
    <ModalWrapper onClose={onClose}>
      <Text className="text-left font-bold text-2xl text-gray-900 mb-4">{ar.walletTransfer}</Text>

      <View className="mb-4">
        <Text className="text-left mb-2 font-semibold text-gray-700">{ar.amount}</Text>
        <Controller
          control={control}
          name="outAmount"
          render={({ field }) => {
            return <PriceInput autoFocus value={field.value as number | undefined} onValueChange={field.onChange} />
          }}
        />
      </View>

      <View className="mb-4">
        <Text className="text-left mb-2 font-semibold text-gray-700">{ar.receivedAmount}</Text>
        <Controller
          control={control}
          name="inAmount"
          render={({ field }) => {
            return <PriceInput value={field.value as number | undefined} onValueChange={field.onChange} />
          }}
        />
      </View>

      <Controller
        control={control}
        name="balanceType"
        render={({ field }) => (
          <View className="flex-row gap-3 mb-4">
            <Pressable
              className={`flex-1 py-2 px-4 rounded border flex-row items-center justify-center ${field.value === "cash" ? "bg-green-50 border-green-500" : "bg-gray-50 border-gray-300"}`}
              onPress={() => field.onChange("cash")}
            >
              <Icon name="cash" size={22} color={field.value === "cash" ? "#22C55E" : "#6B7280"} style={{ marginLeft: 8 }} />
              <Text className={`font-bold text-left text-base ${field.value === "cash" ? "text-green-700" : "text-gray-700"}`}>{ar.cash}</Text>
            </Pressable>
            <Pressable
              className={`flex-1 py-2 px-4 rounded border flex-row items-center justify-center ${field.value === "visa" ? "bg-blue-50 border-blue-500" : "bg-gray-50 border-gray-300"}`}
              onPress={() => field.onChange("visa")}
            >
              <Icon name="credit-card" size={22} color={field.value === "visa" ? "#3B82F6" : "#6B7280"} style={{ marginLeft: 8 }} />
              <Text className={`font-bold text-left text-base ${field.value === "visa" ? "text-blue-700" : "text-gray-700"}`}>{ar.visa}</Text>
            </Pressable>
          </View>
        )}
      />

      <Button onPress={onSubmit} disabled={disabled} loading={fromWalletTransfer.isPending} className="mt-4">
        {ar.transfer}
      </Button>
    </ModalWrapper>
  )
}

function ToWalletTransferModal({ onClose }: { onClose: () => void }) {
  const transferToWallet = trpc.balance.toWalletReceive.useMutation()
  const utils = trpc.useUtils()
  const { control, formState, handleSubmit } = useForm({
    resolver: zodResolver(transferToWalletInput),
  })

  const { isValid } = formState

  const disabled = !isValid

  const onSubmit = handleSubmit((data) => {
    transferToWallet.mutateAsync(data)
    utils.balance.get.invalidate()
    utils.balance.track.invalidate()
    onClose()
  })

  return (
    <ModalWrapper onClose={onClose}>
      <Text className="text-left font-bold text-2xl text-gray-900 mb-4">{ar.walletReceive}</Text>

      <View className="mb-4">
        <Text className="text-left mb-2 font-semibold text-gray-700">{ar.amount}</Text>
        <Controller
          control={control}
          name="outAmount"
          render={({ field }) => {
            return <PriceInput value={field.value as number | undefined} onValueChange={field.onChange} />
          }}
        />
      </View>

      <View className="mb-4">
        <Text className="text-left mb-2 font-semibold text-gray-700">{ar.receivedAmount}</Text>
        <Controller
          control={control}
          name="inAmount"
          render={({ field }) => {
            return <PriceInput value={field.value as number | undefined} onValueChange={field.onChange} />
          }}
        />
      </View>

      <Button onPress={onSubmit} disabled={disabled} loading={transferToWallet.isPending} className="mt-4">
        {ar.receive}
      </Button>
    </ModalWrapper>
  )
}
